import React, { useState, useEffect } from 'react';
import {
  FaLightbulb, FaPlus, FaStar, FaHistory, FaCog,
  FaList, FaTimes, FaCheck, FaTrash, FaEye,
  FaUsers, FaUser, FaTrophy, FaEdit, FaSearch,
  FaChevronDown, FaChevronUp, FaUserCheck, FaBolt,
  FaMedal, FaInbox, FaClipboardCheck, FaBan
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import useAxios from '../utils/useAxios';
import { useSidebar } from '../context/SidebarContext';
import OrganisationSelector from '../components/OrganisationSelector';
import styles from '../styles/RHStatsDetailPage';

// ─── CONSTANTES ──────────────────────────────────────────────────────────────
const C = {
  primary: '#1B019B',
  primaryLight: '#ede9fe',
  success: '#16a34a',
  successLight: '#dcfce7',
  warning: '#d97706',
  warningLight: '#fef3c7',
  danger: '#dc2626',
  dangerLight: '#fee2e2',
  info: '#0891b2',
  infoLight: '#cffafe',
  gray: '#6b7280',
  grayLight: '#f9fafb',
  border: '#e5e7eb',
  white: '#ffffff',
  gold: '#d97706',
  goldLight: '#fef3c7',
  violet: '#7c3aed',
  violetLight: '#f5f3ff',
  emerald: '#059669',
  emeraldLight: '#ecfdf5',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(amount || 0);

// ─── STYLES  ─────────────────────────────────
const inputStyle = {
  width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`,
  borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const btnStyle = (bg, color = '#fff') => ({
  backgroundColor: bg, color, border: 'none', padding: '8px 16px',
  borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
  display: 'inline-flex', alignItems: 'center', gap: '6px',
});
const cardStyle = {
  backgroundColor: C.white, borderRadius: '14px',
  border: `1px solid ${C.border}`, padding: '20px', marginBottom: '20px',
};
const tableHeaderStyle = { padding: '12px 14px', textAlign: 'left', fontSize: '13px', fontWeight: '600' };
const tableCellStyle = { padding: '11px 14px', fontSize: '14px', borderBottom: `1px solid ${C.border}` };

// ─── BADGE STATUT  ─────
const StatutBadge = ({ statut }) => {
  const map = {
    soumise:         { bg: C.infoLight,    color: C.info,    label: 'Soumise' },
    en_evaluation:   { bg: C.warningLight, color: C.warning, label: 'En évaluation' },
    validee:         { bg: C.successLight, color: C.success, label: 'Validée' },
    rejetee:         { bg: C.dangerLight,  color: C.danger,  label: 'Rejetée' },
    prime_attribuee: { bg: '#fdf4ff',      color: '#9333ea', label: 'Prime attribuée' },
  };
  const s = map[statut] || { bg: '#f3f4f6', color: C.gray, label: statut };
  return (
    <span style={{
      backgroundColor: s.bg, color: s.color, padding: '3px 10px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '600',
    }}>{s.label}</span>
  );
};

// ─── MODALE GENERIQUE  ────────────────────────────────
const Modal = ({ show, onClose, title, children, footer, size = '600px' }) => {
  if (!show) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        backgroundColor: C.white, borderRadius: '16px', padding: '24px',
        width: '95%', maxWidth: size, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: `2px solid ${C.primary}`, paddingBottom: '12px', marginBottom: '20px',
        }}>
          <h3 style={{ margin: 0, color: C.primary, fontSize: '17px' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: C.gray }}>
            <FaTimes />
          </button>
        </div>
        <div>{children}</div>
        {footer && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: `1px solid ${C.border}`, paddingTop: '15px', marginTop: '20px' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── LABEL + CHAMP ───────────────────────────────────
const Field = ({ label, required, children }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
      {label} {required && <span style={{ color: C.danger }}>*</span>}
    </label>
    {children}
  </div>
);

// PAGE PRINCIPALE
const GestionInnovationPage = () => {
  const axiosInstance = useAxios();
  const { sidebarVisible } = useSidebar();
  const [activeTab, setActiveTab] = useState('liste');
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);

  // Données
  const [innovations, setInnovations]   = useState([]);
  const [employes, setEmployes]         = useState([]);
  const [criteres, setCriteres]         = useState([]);
  const [regles, setRegles]             = useState([]);
  const [historique, setHistorique]     = useState([]);
  const [loading, setLoading]           = useState(false);

  // Modales
  const [showDetailModal, setShowDetailModal]           = useState(false);
  const [showEvaluerModal, setShowEvaluerModal]         = useState(false);
  const [showApercuModal, setShowApercuModal]           = useState(false);
  const [showCritereModal, setShowCritereModal]         = useState(false);
  const [showRegleModal, setShowRegleModal]             = useState(false);
  const [showDesignerModal, setShowDesignerModal]       = useState(false);

  // Sélection courante
  const [innovationCourante, setInnovationCourante] = useState(null);
  const [apercuData, setApercuData]                 = useState(null);
  const [selectedEvaluateurs, setSelectedEvaluateurs] = useState([]);

  // Formulaire enregistrement
  const [formInn, setFormInn] = useState({
    titre: '', description: '', domaine: '', auteur_id: '',
  });

  // Formulaire évaluation
  const [formEval, setFormEval] = useState({ evaluateur_id: '', commentaire: '', notes: [] });

  // Formulaire critère
  const [formCritere, setFormCritere] = useState({ nom: '', note_max: 10, description: '' });
  const [editCritere, setEditCritere] = useState(null);

  // Formulaire règle prime
  const [formRegle, setFormRegle]   = useState({ score_min: '', score_max: '', montant: '' });
  const [editRegle, setEditRegle]   = useState(null);

  // Filtres liste
  const [searchInn, setSearchInn]     = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');

  // ── EFFETS ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedOrganisation) {
      fetchInnovations();
      fetchEmployes();
      fetchCriteres();
      fetchRegles();
      fetchHistorique();
    }
  }, [selectedOrganisation]);

  useEffect(() => {
    if (selectedOrganisation) fetchInnovations();
  }, [searchInn, filtreStatut]);

  // ── FETCH ──────────────────────────────────────────────────────────────────
  const fetchInnovations = async () => {
    setLoading(true);
    try {
      const params = { organisation: selectedOrganisation };
      if (searchInn) params.search = searchInn;
      if (filtreStatut) params.statut = filtreStatut;
      const res = await axiosInstance.get('/innovation/innovations/', { params });
      setInnovations(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (e) { 
      console.error(e); 
      setInnovations([]);
    }
    finally { setLoading(false); }
  };

  const fetchEmployes = async () => {
      try {
        const res = await axiosInstance.get(`/employes/?organisation=${selectedOrganisation}`);
        setEmployes(Array.isArray(res.data) ? res.data : (res.data.results || []));
      } catch (e) { console.error(e); }
  };

  const fetchCriteres = async () => {
    try {
      const res = await axiosInstance.get('/innovation/innovations/criteres/', { params: { organisation: selectedOrganisation } });
      setCriteres(Array.isArray(res.data) ? res.data : []);
    } catch (e) { 
      console.error(e); 
      setCriteres([]);
    }
  };

  const fetchRegles = async () => {
    try {
      const res = await axiosInstance.get('/innovation/innovations/regles-prime/', { params: { organisation: selectedOrganisation } });
      setRegles(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (e) { 
      console.error(e);
      setRegles([]);
    }
  };

  const fetchHistorique = async () => {
    try {
      const res = await axiosInstance.get('/innovation/innovations/historique-primes/', { params: { organisation: selectedOrganisation } });
      setHistorique(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (e) { 
      console.error(e);
      setHistorique([]);
    }
  };

  // ── ENREGISTRER INNOVATION ─────────────────────────────────────────────────
  const handleEnregistrerInnovation = async () => {
    const { titre, description, auteur_id } = formInn;

    if (!titre || !description || !auteur_id) {
      return Swal.fire({ icon: 'error', title: 'Champs requis', text: 'Titre, description et auteur sont requis' });
    }

    try {
      await axiosInstance.post('/innovation/innovations/', {
        ...formInn, organisation: selectedOrganisation,
      });
      Swal.fire({ icon: 'success', title: 'Succès', text: 'Innovation enregistrée avec succès' });
      setActiveTab('liste');
      setFormInn({ titre: '', description: '', domaine: '', auteur_id: '' });
      fetchInnovations();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    }
  };

  // ── CHANGER STATUT ─────────────────────────────────────────────────────────
  const handleChangerStatut = async (inn, statut, successMsg) => {
    try {
      const res = await axiosInstance.post(`/innovation/innovations/${inn.id}/changer-statut/`, { statut });
      fetchInnovations();
      fetchHistorique();
      if (innovationCourante?.id === inn.id) {
        const updated = await axiosInstance.get(`/innovation/innovations/${inn.id}/`);
        setInnovationCourante(updated.data);
      }
      if (statut === 'validee' && res.data.prime) {
        Swal.fire({
          icon: 'success',
          title: 'Innovation validée !',
          html: `<div style="text-align:center">
            Prime attribuée : <strong>${formatCurrency(res.data.prime.montant_total)}</strong><br>
            Score final : <strong>${res.data.prime.score_final}</strong>
          </div>`,
        });
      } else {
        Swal.fire({ icon: 'success', title: successMsg || 'Statut mis à jour', timer: 1600, showConfirmButton: false });
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    }
  };

  // ── DÉSIGNER ÉVALUATEURS ───────────────────────────────────────────────────
  const handleDesignerEvaluateurs = async () => {
    if (!innovationCourante) return;
    try {
      await axiosInstance.post(`/innovation/innovations/${innovationCourante.id}/designer-evaluateurs/`, {
        evaluateur_ids: selectedEvaluateurs,
      });
      Swal.fire({ icon: 'success', title: 'Évaluateurs désignés', timer: 1600, showConfirmButton: false });
      setShowDesignerModal(false);
      fetchInnovations();
      const updated = await axiosInstance.get(`/innovation/innovations/${innovationCourante.id}/`);
      setInnovationCourante(updated.data);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    }
  };

  // ── EVALUER ────────────────────────────────────────────────────────────────
  const handleEvaluer = async () => {
    const { evaluateur_id, notes, commentaire } = formEval;
    if (!evaluateur_id) {
      return Swal.fire({ icon: 'error', title: 'Champs requis', text: 'Évaluateur requis' });
    }
    const criteresActifs = criteres.filter(c => c.est_actif);
    const notesManquantes = criteresActifs.filter(c => !notes.find(n => n.critere_id === c.id));
    if (notesManquantes.length > 0) {
      return Swal.fire({ icon: 'error', title: 'Notes incomplètes', text: `Veuillez noter tous les critères actifs` });
    }
    try {
      const res = await axiosInstance.post(`/innovation/innovations/${innovationCourante.id}/evaluer/`, {
        evaluateur_id, commentaire, notes,
      });
      Swal.fire({
        icon: 'success', title: 'Score enregistré',
        html: `Score : <strong>${res.data.score_total}</strong> | Moyenne : <strong>${res.data.score_moyen_innovation}</strong>`,
        timer: 2200, showConfirmButton: false,
      });
      setShowEvaluerModal(false);
      setFormEval({ evaluateur_id: '', commentaire: '', notes: [] });
      fetchInnovations();
      const updated = await axiosInstance.get(`/innovation/innovations/${innovationCourante.id}/`);
      setInnovationCourante(updated.data);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    }
  };

  // ── APERCU PRIME ───────────────────────────────────────────────────────────
  const handleApercuPrime = async (inn) => {
    try {
      const res = await axiosInstance.get(`/innovation/innovations/${inn.id}/apercu-prime/`);
      setApercuData(res.data);
      setInnovationCourante(inn);
      setShowApercuModal(true);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    }
  };

  // ── SUPPRIMER INNOVATION ───────────────────────────────────────────────────
  const handleSupprimerInnovation = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette innovation ?',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: C.danger, cancelButtonColor: C.gray,
      confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/innovation/innovations/${id}/`);
      fetchInnovations();
      Swal.fire({ icon: 'success', title: 'Supprimé', text: 'Innovation supprimée' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    }
  };

  // ── CRITERES ───────────────────────────────────────────────────────────────
  const handleSauvegarderCritere = async () => {
    if (!formCritere.nom) return Swal.fire({ icon: 'error', title: 'Erreur', text: 'Nom requis' });
    try {
      if (editCritere) {
        await axiosInstance.put(`/innovation/innovations/criteres/${editCritere.id}/`, formCritere);
      } else {
        await axiosInstance.post('/innovation/innovations/criteres/', { ...formCritere, organisation: selectedOrganisation });
      }
      fetchCriteres();
      setShowCritereModal(false);
      setFormCritere({ nom: '', note_max: 10, description: '' });
      setEditCritere(null);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    }
  };

  const handleSupprimerCritere = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer ce critère ?', icon: 'warning', showCancelButton: true,
      confirmButtonColor: C.danger, cancelButtonColor: C.gray,
      confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/innovation/innovations/criteres/${id}/`);
      fetchCriteres();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    }
  };

  // ── REGLES PRIME ───────────────────────────────────────────────────────────
  const handleSauvegarderRegle = async () => {
    const { score_min, score_max, montant } = formRegle;
    if (!score_min || !score_max || !montant) return Swal.fire({ icon: 'error', title: 'Erreur', text: 'Tous les champs sont requis' });
    try {
      if (editRegle) {
        await axiosInstance.put(`/innovation/innovations/regles-prime/${editRegle.id}/`, formRegle);
      } else {
        await axiosInstance.post('/innovation/innovations/regles-prime/', { ...formRegle, organisation: selectedOrganisation });
      }
      fetchRegles();
      setShowRegleModal(false);
      setFormRegle({ score_min: '', score_max: '', montant: '' });
      setEditRegle(null);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    }
  };

  const handleSupprimerRegle = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette règle ?', icon: 'warning', showCancelButton: true,
      confirmButtonColor: C.danger, cancelButtonColor: C.gray,
      confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/innovation/innovations/regles-prime/${id}/`);
      fetchRegles();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    }
  };

  // ── OUVRIR DETAIL ──────────────────────────────────────────────────────────
  const handleVoirDetail = async (inn) => {
    try {
      const res = await axiosInstance.get(`/innovation/innovations/${inn.id}/`);
      setInnovationCourante(res.data);
      setShowDetailModal(true);
    } catch (e) { console.error(e); }
  };

  // ── NOTE CRITERE ───────────────────────────────────────────────────────────
  const updateNote = (critere_id, note) => {
    setFormEval(f => {
      const notes = f.notes.filter(n => n.critere_id !== critere_id);
      return { ...f, notes: [...notes, { critere_id, note: Number(note) }] };
    });
  };
  const getNoteValue = (critere_id) => {
    const found = formEval.notes.find(n => n.critere_id === critere_id);
    return found ? found.note : '';
  };

  // ── TABS  ──────────────────────────────────────────
  const tabs = [
    { key: 'liste',        label: 'Innovations',     icon: <FaList /> },
    { key: 'enregistrer',  label: 'Proposer',     icon: <FaPlus /> },
    { key: 'evaluations',  label: 'Évaluations',     icon: <FaStar /> },
    { key: 'historique',   label: 'Historique primes',icon: <FaHistory /> },
    { key: 'parametrage',  label: 'Paramétrage',     icon: <FaCog /> },
  ];

  return (
    <div style={{ ...styles.container, marginLeft: sidebarVisible ? 250 : 0 }}>

      {/* HEADER */}
      <div className="text-center mb-3">
        <h3 style={styles.header}>Gestion des Primes d'Innovation</h3>
        <hr style={styles.hr} />
      </div>

      <div style={styles.organisationHeader}>
        <OrganisationSelector
          onOrganisationChange={(id, org) => { setSelectedOrganisation(id); setCurrentOrganisation(org); }}
          selectedOrganisation={selectedOrganisation}
        />
      </div>

      {!currentOrganisation ? (
        <div style={{ textAlign: 'center', padding: '60px', color: C.gray }}>
          Sélectionnez une organisation pour commencer
        </div>
      ) : (
        <>
          <h1 style={{ color: C.primary, fontSize: '26px', fontWeight: 'bold', marginBottom: '20px' }}>
            <FaLightbulb style={{ marginRight: '10px', color: '#f59e0b' }} />
            PRIMES D'INNOVATION — {currentOrganisation.nom}
          </h1>

          {/* TABS */}
          <div style={{ borderBottom: `2px solid ${C.primary}`, marginBottom: '24px', display: 'flex', gap: '4px', overflowX: 'auto' }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: '14px',
                fontWeight: activeTab === t.key ? '700' : '500',
                color: activeTab === t.key ? C.primary : C.gray,
                borderBottom: activeTab === t.key ? `3px solid ${C.primary}` : '3px solid transparent',
                background: 'none', display: 'flex', alignItems: 'center', gap: '7px',
                whiteSpace: 'nowrap',
              }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* ── ONGLET LISTE (comme dans le premier) ───────────────────────────── */}
          {activeTab === 'liste' && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                <h5 style={{ margin: 0, color: C.primary }}>
                  <FaList style={{ marginRight: '8px' }} />Liste des innovations
                </h5>
                <button style={btnStyle(C.primary)} onClick={() => setActiveTab('enregistrer')}>
                  <FaPlus /> Proposer une innovation
                </button>
              </div>

              {/* Filtres */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaSearch style={{ color: C.primary }} />
                  <input
                    type="text" placeholder="Rechercher..." value={searchInn}
                    onChange={e => setSearchInn(e.target.value)}
                    style={{ ...inputStyle, width: '220px' }}
                  />
                </div>
                <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}
                  style={{ ...inputStyle, width: '180px' }}>
                  <option value="">Tous les statuts</option>
                  <option value="soumise">Soumise</option>
                  <option value="en_evaluation">En évaluation</option>
                  <option value="validee">Validée</option>
                  <option value="rejetee">Rejetée</option>
                </select>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: C.gray }}>Chargement...</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: C.primary, color: C.white }}>
                        <th style={tableHeaderStyle}>Titre</th>
                        <th style={tableHeaderStyle}>Innovateuur</th>
                        <th style={tableHeaderStyle}>Domaine</th>
                        <th style={tableHeaderStyle}>Statut</th>
                        <th style={tableHeaderStyle}>Score moy.</th>
                        <th style={tableHeaderStyle}>Évaluateurs</th>
                        <th style={tableHeaderStyle}>Date</th>
                        <th style={tableHeaderStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {innovations.map(inn => (
                        <tr key={inn.id} style={{ backgroundColor: C.white }}>
                          <td style={tableCellStyle}>
                            <strong>{inn.titre}</strong>
                            {inn.prime && (
                              <div style={{ fontSize: '12px', color: C.success, marginTop: '3px' }}>
                                Prime : {formatCurrency(inn.prime.montant_total)}
                              </div>
                            )}
                          </td>
                          <td style={tableCellStyle}>{inn.auteur_prenom} {inn.auteur_nom}</td>
                          <td style={tableCellStyle}>{inn.domaine || '—'}</td>
                          <td style={tableCellStyle}><StatutBadge statut={inn.statut} /></td>
                          <td style={{ ...tableCellStyle, fontWeight: 'bold', color: inn.score_moyen ? C.primary : C.gray }}>
                            {inn.score_moyen ?? '—'}
                          </td>
                          <td style={tableCellStyle}>{inn.nombre_evaluateurs}</td>
                          <td style={tableCellStyle}>{inn.date_soumission}</td>
                          <td style={tableCellStyle}>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              <button style={btnStyle(C.primary)} onClick={() => handleVoirDetail(inn)} title="Voir détail">
                                <FaEye />
                              </button>
                              {inn.statut !== 'validee' && inn.statut !== 'rejetee' && (
                                <button style={btnStyle(C.danger)} onClick={() => handleSupprimerInnovation(inn.id)} title="Supprimer">
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {innovations.length === 0 && (
                        <tr>
                          <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: C.gray }}>
                            Aucune innovation enregistrée
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'enregistrer' && (
            <div style={cardStyle}>
              <h5 style={{ color: C.primary, marginBottom: '20px' }}>
                <FaPlus style={{ marginRight: '8px' }} />Enregistrer une nouvelle innovation
              </h5>

              <Field label="Titre de l'innovation" required>
                <input type="text" style={inputStyle} value={formInn.titre}
                  onChange={e => setFormInn(f => ({ ...f, titre: e.target.value }))}
                  placeholder="Ex: Nouveau système de gestion ..." />
              </Field>

              <Field label="Domaine">
                <input type="text" style={inputStyle} value={formInn.domaine}
                  onChange={e => setFormInn(f => ({ ...f, domaine: e.target.value }))}
                  placeholder="Ex: Logistique, RH, IT..." />
              </Field>

              <Field label="Innovateur" required>
                <select style={inputStyle} value={formInn.auteur_id}
                  onChange={e => setFormInn(f => ({ ...f, auteur_id: e.target.value }))}>
                  <option value="">-- Sélectionner l'innovateur --</option>
                  {employes.map(e => (
                    <option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.matricule}</option>
                  ))}
                </select>
              </Field>

              <Field label="Description détaillée" required>
                <textarea style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                  value={formInn.description}
                  onChange={e => setFormInn(f => ({ ...f, description: e.target.value }))}
                  placeholder="Décrivez l'innovation en détail : contexte, fonctionnement, bénéfices attendus..." />
              </Field>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button style={btnStyle(C.gray)} onClick={() => setFormInn({ titre: '', description: '', domaine: '', auteur_id: '' })}>
                  Réinitialiser
                </button>
                <button style={btnStyle(C.primary)} onClick={handleEnregistrerInnovation}>
                  <FaCheck /> Enregistrer l'innovation
                </button>
              </div>
            </div>
          )}

          {/* ── ONGLET EVALUATIONS ─────────────────────────────────────────────── */}
          {activeTab === 'evaluations' && (
            <div style={cardStyle}>
              <h5 style={{ color: C.primary, marginBottom: '16px' }}>
                <FaStar style={{ marginRight: '8px' }} />Innovations à évaluer
              </h5>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: C.primary, color: C.white }}>
                      <th style={tableHeaderStyle}>Innovation</th>
                      <th style={tableHeaderStyle}>Innovateur</th>
                      <th style={tableHeaderStyle}>Statut</th>
                      <th style={tableHeaderStyle}>Score moyen</th>
                      <th style={tableHeaderStyle}>Évaluateurs</th>
                      <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {innovations.filter(i => i.statut !== 'rejetee' && i.statut !== 'validee').map(inn => (
                      <tr key={inn.id} style={{ backgroundColor: C.white }}>
                        <td style={tableCellStyle}>
                          <strong>{inn.titre}</strong>
                          <div style={{ fontSize: '12px', color: C.gray }}>{inn.domaine || '—'}</div>
                        </td>
                        <td style={tableCellStyle}>{inn.auteur_prenom} {inn.auteur_nom}</td>
                        <td style={tableCellStyle}><StatutBadge statut={inn.statut} /></td>
                        <td style={{ ...tableCellStyle, fontWeight: 'bold', color: inn.score_moyen ? C.primary : C.gray }}>
                          {inn.score_moyen ?? '—'}
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {(inn.evaluateurs_designes || []).map(ev => (
                              <span key={ev.id} style={{ background: C.primaryLight, borderRadius: '20px', padding: '2px 8px', fontSize: '11px', color: C.primary }}>
                                {ev.prenom} {ev.nom}
                              </span>
                            ))}
                            {(inn.evaluateurs_designes || []).length === 0 && <span style={{ color: C.gray }}>—</span>}
                          </div>
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {inn.statut === 'soumise' && (
                              <button style={btnStyle(C.info)} onClick={() => handleChangerStatut(inn, 'en_evaluation', 'Évaluation ouverte')}>
                                <FaBolt /> Démarrer
                              </button>
                            )}
                            <button style={btnStyle(C.violet)} onClick={() => {
                              setInnovationCourante(inn);
                              setSelectedEvaluateurs((inn.evaluateurs_designes || []).map(e => e.id));
                              setShowDesignerModal(true);
                            }}>
                              <FaUserCheck /> Désigner
                            </button>
                            {inn.statut === 'en_evaluation' && (
                              <button style={btnStyle(C.warning)} onClick={() => {
                                setInnovationCourante(inn);
                                setFormEval({ evaluateur_id: '', commentaire: '', notes: [] });
                                setShowEvaluerModal(true);
                              }}>
                                <FaStar /> Saisir score
                              </button>
                            )}
                            {inn.statut === 'en_evaluation' && inn.score_moyen != null && (
                              <>
                                <button style={btnStyle(C.gold)} onClick={() => handleApercuPrime(inn)}>
                                  <FaTrophy /> Aperçu
                                </button>
                                <button style={btnStyle(C.success)} onClick={() => handleChangerStatut(inn, 'validee')}>
                                  <FaCheck /> Valider
                                </button>
                              </>
                            )}
                            {['soumise', 'en_evaluation'].includes(inn.statut) && (
                              <button style={btnStyle(C.danger)} onClick={() => handleChangerStatut(inn, 'rejetee', 'Innovation rejetée')}>
                                <FaTimes /> Rejeter
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {innovations.filter(i => i.statut !== 'rejetee' && i.statut !== 'validee').length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: C.gray }}>
                          Aucune innovation en cours d'évaluation
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── ONGLET HISTORIQUE ───────────────────────────────────────────────── */}
          {activeTab === 'historique' && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h5 style={{ margin: 0, color: C.primary }}>
                  <FaHistory style={{ marginRight: '8px' }} />Historique des primes d'innovation
                </h5>
                <div style={{
                  backgroundColor: C.primaryLight, color: C.primary,
                  padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '15px',
                }}>
                  Total : {formatCurrency(historique.reduce((s, p) => s + (p.montant_total || 0), 0))}
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: C.primary, color: C.white }}>
                      <th style={tableHeaderStyle}>Innovation</th>
                      <th style={tableHeaderStyle}>Bénéficiaire</th>
                      <th style={tableHeaderStyle}>Score final</th>
                      <th style={tableHeaderStyle}>Prime</th>
                      <th style={tableHeaderStyle}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historique.map(prime => (
                      <tr key={prime.id} style={{ backgroundColor: C.white }}>
                        <td style={tableCellStyle}><strong>{prime.innovation_titre}</strong></td>
                        <td style={tableCellStyle}>{prime.auteur_prenom} {prime.auteur_nom}</td>
                        <td style={{ ...tableCellStyle, fontWeight: 'bold', color: C.primary }}>{prime.score_final}</td>
                        <td style={{ ...tableCellStyle, fontWeight: 'bold', color: C.success }}>{formatCurrency(prime.montant_total)}</td>
                        <td style={tableCellStyle}>{prime.date_attribution}</td>
                      </tr>
                    ))}
                    {historique.length === 0 && (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: C.gray }}>
                          Aucune prime attribuée pour le moment
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── ONGLET PARAMETRAGE ───────────────────────────────────────────────── */}
          {activeTab === 'parametrage' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h5 style={{ margin: 0, color: C.primary }}>
                    <FaStar style={{ marginRight: '8px' }} />Critères d'évaluation
                  </h5>
                  <button style={btnStyle(C.primary)} onClick={() => { setEditCritere(null); setFormCritere({ nom: '', note_max: 10, description: '' }); setShowCritereModal(true); }}>
                    <FaPlus /> Ajouter
                  </button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: C.grayLight }}>
                      <th style={tableHeaderStyle}>Critère</th>
                      <th style={tableHeaderStyle}>Note max</th>
                      <th style={tableHeaderStyle}>Actif</th>
                      <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {criteres.map(c => (
                      <tr key={c.id}>
                        <td style={tableCellStyle}><strong>{c.nom}</strong></td>
                        <td style={tableCellStyle}>{c.note_max}</td>
                        <td style={tableCellStyle}>
                          {c.est_actif
                            ? <span style={{ color: C.success }}><FaCheck /></span>
                            : <span style={{ color: C.danger }}><FaTimes /></span>}
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button style={btnStyle(C.warning)} onClick={() => { setEditCritere(c); setFormCritere({ nom: c.nom, note_max: c.note_max, description: c.description || '' }); setShowCritereModal(true); }}>
                              <FaEdit />
                            </button>
                            <button style={btnStyle(C.danger)} onClick={() => handleSupprimerCritere(c.id)}>
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {criteres.length === 0 && (
                      <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: C.gray }}>Aucun critère configuré</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h5 style={{ margin: 0, color: C.primary }}>
                    <FaTrophy style={{ marginRight: '8px' }} />Barème des primes
                  </h5>
                  <button style={btnStyle(C.primary)} onClick={() => { setEditRegle(null); setFormRegle({ score_min: '', score_max: '', montant: '' }); setShowRegleModal(true); }}>
                    <FaPlus /> Ajouter
                  </button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: C.grayLight }}>
                      <th style={tableHeaderStyle}>Score min</th>
                      <th style={tableHeaderStyle}>Score max</th>
                      <th style={tableHeaderStyle}>Montant</th>
                      <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regles.map(r => (
                      <tr key={r.id}>
                        <td style={tableCellStyle}>{r.score_min}</td>
                        <td style={tableCellStyle}>{r.score_max}</td>
                        <td style={{ ...tableCellStyle, fontWeight: 'bold', color: C.success }}>{formatCurrency(r.montant)}</td>
                        <td style={tableCellStyle}>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button style={btnStyle(C.warning)} onClick={() => { setEditRegle(r); setFormRegle({ score_min: r.score_min, score_max: r.score_max, montant: r.montant }); setShowRegleModal(true); }}>
                              <FaEdit />
                            </button>
                            <button style={btnStyle(C.danger)} onClick={() => handleSupprimerRegle(r.id)}>
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {regles.length === 0 && (
                      <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: C.gray }}>Aucun barème configuré</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}


          {/* Modale Détail Innovation */}
          <Modal show={showDetailModal} onClose={() => setShowDetailModal(false)}
            title={`Détail — ${innovationCourante?.titre}`} size="750px">
            {innovationCourante && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  {[
                    ['Statut', <StatutBadge statut={innovationCourante.statut} />],
                    ['Score moyen', innovationCourante.score_moyen ?? '—'],
                    ['Évaluateurs', innovationCourante.nombre_evaluateurs],
                    ['Innovateur', `${innovationCourante.auteur_prenom || ''} ${innovationCourante.auteur_nom || ''}`],
                    ['Domaine', innovationCourante.domaine || '—'],
                    ['Date', innovationCourante.date_soumission],
                  ].map(([k, v], i) => (
                    <div key={i} style={{ backgroundColor: C.grayLight, borderRadius: '8px', padding: '10px' }}>
                      <div style={{ fontSize: '11px', color: C.gray, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{k}</div>
                      <div style={{ fontWeight: '600', marginTop: '3px' }}>{v}</div>
                    </div>
                  ))}
                </div>

                <div style={{ backgroundColor: C.grayLight, borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', color: C.gray, textTransform: 'uppercase', marginBottom: '5px' }}>Description</div>
                  <p style={{ margin: 0 }}>{innovationCourante.description}</p>
                </div>

                {(innovationCourante.evaluations || []).length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong style={{ color: C.primary }}>Scores des évaluateurs</strong>
                    {(innovationCourante.evaluations || []).map((ev, i) => (
                      <div key={i} style={{ backgroundColor: C.grayLight, borderRadius: '8px', padding: '12px', marginTop: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <strong>{ev.evaluateur_prenom} {ev.evaluateur_nom}</strong>
                          <span style={{ color: C.primary, fontWeight: '700' }}>Score total : {ev.score_total}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {ev.notes.map((n, j) => (
                            <span key={j} style={{ backgroundColor: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '4px 10px', fontSize: '13px' }}>
                              {n.critere_nom} : <strong>{n.note}/{n.note_max}</strong>
                            </span>
                          ))}
                        </div>
                        {ev.commentaire && (
                          <p style={{ marginTop: '8px', fontSize: '13px', color: C.gray }}>
                            {ev.commentaire}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {innovationCourante.prime && (
                  <div style={{ background: C.emeraldLight, border: '1px solid #a7f3d0', borderRadius: '12px', padding: '14px 18px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: C.emerald, fontWeight: '600', marginBottom: '4px' }}>
                          Prime attribuée
                        </div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: C.emerald }}>{formatCurrency(innovationCourante.prime.montant_total)}</div>
                      </div>
                      <div style={{ textAlign: 'right', fontSize: '13px', color: C.emerald }}>
                        <div>Score : <strong>{innovationCourante.prime.score_final}</strong></div>
                        <div>{innovationCourante.prime.date_attribution}</div>
                      </div>
                    </div>
                  </div>
                )}

                {['soumise', 'en_evaluation'].includes(innovationCourante.statut) && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px', borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
                    {innovationCourante.statut === 'soumise' && (
                      <button style={btnStyle(C.info)} onClick={() => handleChangerStatut(innovationCourante, 'en_evaluation', 'Évaluation ouverte')}>
                        <FaBolt /> Démarrer l'évaluation
                      </button>
                    )}
                    <button style={btnStyle(C.violet)} onClick={() => {
                      setSelectedEvaluateurs((innovationCourante.evaluateurs_designes || []).map(e => e.id));
                      setShowDesignerModal(true);
                    }}>
                      <FaUserCheck /> Désigner évaluateurs
                    </button>
                  </div>
                )}
              </div>
            )}
          </Modal>

          {/* Modale Désigner évaluateurs */}
          <Modal show={showDesignerModal} onClose={() => setShowDesignerModal(false)}
            title="Désigner des évaluateurs" size="500px"
            footer={
              <>
                <button style={btnStyle(C.gray)} onClick={() => setShowDesignerModal(false)}>Annuler</button>
                <button style={btnStyle(C.violet)} onClick={handleDesignerEvaluateurs}>
                  <FaUserCheck /> Confirmer
                </button>
              </>
            }>
            <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
              {employes.map(e => {
                const checked = selectedEvaluateurs.includes(e.id);
                return (
                  <label key={e.id} style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
                    borderRadius: '10px', cursor: 'pointer', border: `1.5px solid ${checked ? C.primaryLight : C.border}`,
                    background: checked ? C.primaryLight : C.white, marginBottom: '8px',
                  }}>
                    <input type="checkbox" checked={checked} style={{ width: '16px', height: '16px' }}
                      onChange={() => {
                        setSelectedEvaluateurs(prev =>
                          checked ? prev.filter(id => id !== e.id) : [...prev, e.id]
                        );
                      }} />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{e.first_name} {e.last_name}</div>
                      <div style={{ fontSize: '12px', color: C.gray }}>{e.matricule}</div>
                    </div>
                    {checked && <FaCheck style={{ marginLeft: 'auto', color: C.primary, fontSize: '12px' }} />}
                  </label>
                );
              })}
            </div>
            <div style={{ marginTop: '12px', fontSize: '13px', color: C.gray }}>
              {selectedEvaluateurs.length} évaluateur{selectedEvaluateurs.length > 1 ? 's' : ''} sélectionné{selectedEvaluateurs.length > 1 ? 's' : ''}
            </div>
          </Modal>

          {/* Modale Evaluer */}
          <Modal show={showEvaluerModal} onClose={() => setShowEvaluerModal(false)}
            title={`Saisir un score — ${innovationCourante?.titre}`} size="550px"
            footer={
              <>
                <button style={btnStyle(C.gray)} onClick={() => setShowEvaluerModal(false)}>Annuler</button>
                <button style={btnStyle(C.primary)} onClick={handleEvaluer}><FaCheck /> Enregistrer</button>
              </>
            }>
            <Field label="Évaluateur" required>
              <select style={inputStyle} value={formEval.evaluateur_id}
                onChange={e => setFormEval(f => ({ ...f, evaluateur_id: e.target.value }))}>
                <option value="">-- Sélectionner l'évaluateur --</option>
                {employes.map(e => (
                  <option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.matricule}</option>
                ))}
              </select>
            </Field>

            {criteres.filter(c => c.est_actif).length === 0 ? (
              <div style={{ backgroundColor: C.warningLight, color: C.warning, padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                Aucun critère actif. Configurez d'abord les critères dans l'onglet Paramétrage.
              </div>
            ) : (
              <div style={{ backgroundColor: C.grayLight, borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
                <strong style={{ display: 'block', marginBottom: '12px', color: C.primary }}>Notes par critère</strong>
                {criteres.filter(c => c.est_actif).map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <label style={{ fontWeight: '500', fontSize: '14px' }}>{c.nom} <span style={{ color: C.gray, fontSize: '12px' }}>(/{c.note_max})</span></label>
                    <input type="number" min="0" max={c.note_max} step="0.5"
                      style={{ ...inputStyle, width: '90px', textAlign: 'center' }}
                      value={getNoteValue(c.id)}
                      onChange={e => updateNote(c.id, e.target.value)}
                      placeholder="0" />
                  </div>
                ))}
                <div style={{ textAlign: 'right', fontWeight: '700', color: C.primary, borderTop: `1px solid ${C.border}`, paddingTop: '8px' }}>
                  Score total estimé : {formEval.notes.reduce((s, n) => s + Number(n.note || 0), 0)} / {criteres.filter(c => c.est_actif).reduce((s, c) => s + c.note_max, 0)}
                </div>
              </div>
            )}

            <Field label="Commentaire">
              <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                value={formEval.commentaire}
                onChange={e => setFormEval(f => ({ ...f, commentaire: e.target.value }))}
                placeholder="Observations ou remarques sur cette innovation..." />
            </Field>
          </Modal>

          {/* Modale Aperçu Prime */}
          <Modal show={showApercuModal} onClose={() => setShowApercuModal(false)}
            title="Aperçu de la prime" size="500px"
            footer={
              <>
                <button style={btnStyle(C.gray)} onClick={() => setShowApercuModal(false)}>Annuler</button>
                <button style={btnStyle(C.success)} onClick={() => {
                  setShowApercuModal(false);
                  innovationCourante && handleChangerStatut(innovationCourante, 'validee');
                }}>
                  <FaCheck /> Valider et attribuer
                </button>
              </>
            }>
            {apercuData && (
              <div>
                <div style={{ backgroundColor: C.primaryLight, borderRadius: '10px', padding: '16px', marginBottom: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', color: C.gray }}>Score moyen ({apercuData.nombre_evaluateurs} évaluateur{apercuData.nombre_evaluateurs > 1 ? 's' : ''})</div>
                  <div style={{ fontSize: '36px', fontWeight: '800', color: C.primary }}>{apercuData.score_moyen}</div>
                </div>

                {apercuData.regle_appliquee && (
                  <div style={{ backgroundColor: '#fdf4ff', border: '1px solid #d8b4fe', borderRadius: '10px', padding: '14px', marginBottom: '16px', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', color: '#7c3aed' }}>Règle appliquée (score {apercuData.regle_appliquee.score_min} – {apercuData.regle_appliquee.score_max})</div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#9333ea' }}>{formatCurrency(apercuData.montant_estime || apercuData.montant_total)}</div>
                  </div>
                )}

                <strong style={{ display: 'block', marginBottom: '10px', color: C.primary }}>Bénéficiaire</strong>
                {apercuData.beneficiaire && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: C.grayLight, borderRadius: '8px' }}>
                    <div>
                      <strong>{apercuData.beneficiaire.prenom} {apercuData.beneficiaire.nom}</strong>
                    </div>
                    <span style={{ fontWeight: '700', color: C.success }}>{formatCurrency(apercuData.beneficiaire.montant)}</span>
                  </div>
                )}
              </div>
            )}
          </Modal>

          {/* Modale Critère */}
          <Modal show={showCritereModal} onClose={() => setShowCritereModal(false)}
            title={editCritere ? 'Modifier le critère' : 'Nouveau critère'} size="450px"
            footer={
              <>
                <button style={btnStyle(C.gray)} onClick={() => setShowCritereModal(false)}>Annuler</button>
                <button style={btnStyle(C.primary)} onClick={handleSauvegarderCritere}><FaCheck /> Sauvegarder</button>
              </>
            }>
            <Field label="Nom du critère" required>
              <input type="text" style={inputStyle} value={formCritere.nom}
                onChange={e => setFormCritere(f => ({ ...f, nom: e.target.value }))}
                placeholder="Ex: Impact, Originalité, Gain financier..." />
            </Field>
            <Field label="Note maximale" required>
              <input type="number" min="1" max="100" style={inputStyle} value={formCritere.note_max}
                onChange={e => setFormCritere(f => ({ ...f, note_max: Number(e.target.value) }))} />
            </Field>
            <Field label="Description">
              <textarea style={{ ...inputStyle, minHeight: '70px' }} value={formCritere.description}
                onChange={e => setFormCritere(f => ({ ...f, description: e.target.value }))}
                placeholder="Description optionnelle du critère..." />
            </Field>
          </Modal>

          {/* Modale Règle Prime */}
          <Modal show={showRegleModal} onClose={() => setShowRegleModal(false)}
            title={editRegle ? 'Modifier la règle' : 'Nouvelle règle de prime'} size="450px"
            footer={
              <>
                <button style={btnStyle(C.gray)} onClick={() => setShowRegleModal(false)}>Annuler</button>
                <button style={btnStyle(C.primary)} onClick={handleSauvegarderRegle}><FaCheck /> Sauvegarder</button>
              </>
            }>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="Score minimum" required>
                <input type="number" min="0" step="0.01" style={inputStyle} value={formRegle.score_min}
                  onChange={e => setFormRegle(f => ({ ...f, score_min: e.target.value }))}
                  placeholder="Ex: 21" />
              </Field>
              <Field label="Score maximum" required>
                <input type="number" min="0" step="0.01" style={inputStyle} value={formRegle.score_max}
                  onChange={e => setFormRegle(f => ({ ...f, score_max: e.target.value }))}
                  placeholder="Ex: 30" />
              </Field>
            </div>
            <Field label="Montant de la prime (FCFA)" required>
              <input type="number" min="0" style={inputStyle} value={formRegle.montant}
                onChange={e => setFormRegle(f => ({ ...f, montant: e.target.value }))}
                placeholder="Ex: 100000" />
            </Field>
            <div style={{ backgroundColor: C.primaryLight, borderRadius: '8px', padding: '10px', fontSize: '13px', color: C.primary }}>
              ℹ️ Score entre <strong>{formRegle.score_min || '?'}</strong> et <strong>{formRegle.score_max || '?'}</strong> → Prime de <strong>{formatCurrency(formRegle.montant || 0)}</strong>
            </div>
          </Modal>

        </>
      )}
    </div>
  );
};

export default GestionInnovationPage;